import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { REGISTRATION_TOKEN } from '@libs/registration';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { Configuration, ErrorCode, ErrorResponse } from '@ffd/api-client';
import { FORCE_REFRESH_TOKEN, TokenService } from '@libs/shared-services';
import { SITE_ASSOCIATION_TOKEN, SITE_ASSOCIATION_WITH_ACCESS_TOKEN, TWO_FA_TOKEN_SITE_ASSOC_AUTH } from '@libs/site-association';
import { CONTRACT_SIGN_TOKEN } from '@libs/site-setup';
import { FFD_NOTES_INVITATION_TOKEN, FFD_NOTES_INVITATION_TWO_FA_TOKEN, NOTES_BAA_SIGN_REQUEST_TOKEN } from '@libs/ffd-notes';
import { X_SOURCE } from '@libs/utils';
import { TWO_FA_TOKEN } from '../../login/verification-screen/verification-screen.constants';
import { FORGOTTEN_PASSWORD_TOKEN } from '../../forgot-password/reset-password-screen/reset-password-screen.constants';

@Injectable({ providedIn: 'root' })
export class TokenInterceptorService implements HttpInterceptor {
  private readonly tokenService = inject(TokenService);
  private readonly configuration = inject(Configuration);
  private readonly publicUrls: string[] = ['assets', 'login'];

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const xSource = req.context.get(X_SOURCE);
    if (xSource) {
      req = req.clone({ headers: req.headers.set('X-Source', xSource) });
    }

    if (
      this.publicUrls.some((it) => req.url.includes(it)) &&
      !req.url.includes('reset-password') &&
      !req.url.includes('site-association') &&
      !req.url.includes('ffd-notes-invitation')
    ) {
      return next.handle(req);
    }

    const siteAssociationWithAccessToken = req.context.get(SITE_ASSOCIATION_WITH_ACCESS_TOKEN);
    if (siteAssociationWithAccessToken === false) {
      let headers = req.headers;
      const twoFaToken = req.context.get(TWO_FA_TOKEN_SITE_ASSOC_AUTH);
      const registrationToken = req.context.get(REGISTRATION_TOKEN);
      const siteAssociationToken = req.context.get(SITE_ASSOCIATION_TOKEN);

      if (twoFaToken) headers = headers.set('Two-Fa-Token', twoFaToken);
      if (registrationToken) headers = headers.set('Registration-Token', registrationToken);
      if (siteAssociationToken) headers = headers.set('Site-Association-Token', siteAssociationToken);

      return next.handle(req.clone({ headers }));
    }

    const registrationToken = req.context.get(REGISTRATION_TOKEN);
    if (registrationToken) {
      return next.handle(req.clone({ headers: req.headers.set('Registration-Token', registrationToken) }));
    }

    const twoFaToken = req.context.get(TWO_FA_TOKEN);
    if (twoFaToken) {
      return next.handle(req.clone({ headers: req.headers.set('Two-Fa-Token', `${twoFaToken}`) }));
    }

    const forgottenPasswordToken = req.context.get(FORGOTTEN_PASSWORD_TOKEN);
    if (forgottenPasswordToken) {
      return next.handle(req.clone({ headers: req.headers.set('Reset-Password-Token', `${forgottenPasswordToken}`) }));
    }

    const contractSignToken = req.context.get(CONTRACT_SIGN_TOKEN);
    if (contractSignToken) {
      return next.handle(req.clone({ headers: req.headers.set('Contract-Signing-Token', contractSignToken) }));
    }

    const notesBaaSignRequestToken = req.context.get(NOTES_BAA_SIGN_REQUEST_TOKEN);
    if (notesBaaSignRequestToken) {
      const baaHeaders = req.headers.set('Notes-Baa-Sign-Request-Token', notesBaaSignRequestToken);
      return this.tokenService.getAccessToken$({ isUserActivity: true }).pipe(
        switchMap((token) => {
          const headers = token ? baaHeaders.set('Authorization', `Bearer ${token}`) : baaHeaders;
          return next.handle(req.clone({ headers }));
        }),
      );
    }

    const ffdNotesInvitationToken = req.context.get(FFD_NOTES_INVITATION_TOKEN);
    if (ffdNotesInvitationToken) {
      let invitationHeaders = req.headers.set('ffd-notes-invitation-token', ffdNotesInvitationToken);
      const ffdNotesInvitationTwoFaToken = req.context.get(FFD_NOTES_INVITATION_TWO_FA_TOKEN);
      if (ffdNotesInvitationTwoFaToken) {
        invitationHeaders = invitationHeaders.set('Two-Fa-Token', ffdNotesInvitationTwoFaToken);
      }

      return this.tokenService.getAccessToken$({ isUserActivity: true }).pipe(
        switchMap((token) => {
          const headers = token ? invitationHeaders.set('Authorization', `Bearer ${token}`) : invitationHeaders;
          return next.handle(req.clone({ headers }));
        }),
      );
    }

    const forceRefreshToken = req.context.get(FORCE_REFRESH_TOKEN);
    if (forceRefreshToken) {
      return next.handle(req.clone({ headers: req.headers.set('Refresh-Token', forceRefreshToken) })).pipe(
        catchError((response) => {
          const isUnauthorized = response instanceof HttpErrorResponse && response.status === HttpStatusCode.Unauthorized;
          if (isUnauthorized) {
            this.tokenService.logout();
          }

          return throwError(() => response);
        }),
      );
    }

    // Check if request needs authentication before calling getAccessToken$()
    if (this.configuration.basePath && !req.url.includes(this.configuration.basePath)) {
      console.warn('Wrong base url - skipping token authentication');
      return next.handle(req);
    }

    return this.tokenService.getAccessToken$({ isUserActivity: true }).pipe(
      switchMap((token) => {
        if (token == null) {
          return next.handle(req);
        }

        const siteAssociationToken = req.context.get(SITE_ASSOCIATION_TOKEN);
        let headers = req.headers.set('Authorization', `Bearer ${token}`);
        if (siteAssociationToken) {
          headers = headers.set('Site-Association-Token', siteAssociationToken);
        }

        return next.handle(req.clone({ headers })).pipe(
          catchError((response: ErrorResponse) => {
            const isUnauthorized = response.statusCode === HttpStatusCode.Unauthorized && response.code === ErrorCode.AUTHENTICATION_FAILED;
            if (isUnauthorized) {
              this.tokenService.logout();
            }

            return throwError(() => response);
          }),
        );
      }),
    );
  }
}
